import React, { useEffect, useMemo, useState } from 'react';
import { PreguntaTrivia } from '../types';

interface Props {
  pregunta: PreguntaTrivia;
  onResponder: (respuesta: string) => void;
  respondida: boolean;
  respuestaJugador: string;
  fueCorrecta: boolean | null;
  onContinuar?: () => void;
  esUltima?: boolean;
  esUltimaDelNivel?: boolean;
  modalidad?: 'classic' | 'nohit';
  puntosGanados?: number;
}

const Level1Trivia: React.FC<Props> = ({
  pregunta,
  onResponder,
  respondida,
  respuestaJugador,
  fueCorrecta,
  onContinuar,
  esUltima,
  esUltimaDelNivel,
  modalidad,
  puntosGanados = 0,
}) => {
  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  const letras = ['A', 'B', 'C', 'D'];

  const opcionesMezcladas = useMemo(() => {
    const copia = [...pregunta.opciones];
    for (let i = copia.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copia[i], copia[j]] = [copia[j], copia[i]];
    }
    return copia;
  }, [pregunta.id, pregunta.opciones]);

  useEffect(() => {
    setSeleccionada(null);
  }, [pregunta.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat) return;

      if (respondida) {
        if (onContinuar) {
          e.preventDefault();
          onContinuar();
        }
        return;
      }

      if (seleccionada) {
        e.preventDefault();
        onResponder(seleccionada);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [respondida, onContinuar, seleccionada, onResponder]);

  const handleClick = (opcion: string) => {
    if (respondida) return;
    setSeleccionada(opcion);
  };

  const getEstiloOpcion = (opcion: string) => {
    const isSelected = seleccionada === opcion;
    const isCorrect = opcion === pregunta.respuesta;
    const isResolved = respondida;
    const isSelectedWrong = isResolved && opcion === respuestaJugador && !fueCorrecta;

    if (isResolved) {
      if (isCorrect) {
        return {
          wrapper: 'border-green-500 bg-green-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-correcto',
          circle: 'border-green-500 bg-green-500 text-white',
        };
      }
      if (isSelectedWrong) {
        return {
          wrapper: 'border-red-500 bg-red-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-incorrecto',
          circle: 'border-red-500 bg-red-500 text-white',
        };
      }
      return {
        wrapper: 'border-gray-400 bg-gray-100',
        text: 'opcion-texto-resuelto',
        circle: 'border-gray-400 bg-gray-400 text-white',
      };
    }

    if (isSelected) {
      return {
        wrapper: 'border-yellow-500 bg-yellow-100 shadow-lg scale-[1.01]',
        text: 'opcion-texto-seleccionado',
        circle: 'border-yellow-500 bg-yellow-500 text-white',
      };
    }

    return {
      wrapper: 'border-slate-300 bg-white hover:border-emerald-300',
      text: 'opcion-texto-normal',
      circle: 'border-slate-400 text-slate-500 bg-white',
    };
  };

  return (
    <div className="animate-fade-in">
      <div className="pregunta-enhanced p-6 text-center shadow-lg mb-8 rounded-xl">
        <h2 className="text-xl md:text-2xl font-medium texto-campo-enhanced leading-relaxed">
          {pregunta.texto}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {opcionesMezcladas.map((opcion, i) => (
          (() => {
            const estilo = getEstiloOpcion(opcion);
            return (
              <button
                key={i}
                onClick={() => handleClick(opcion)}
                disabled={respondida}
                className={`opcion-enhanced p-4 rounded-lg border-2 transition-all duration-200 min-h-[84px] flex items-center w-full text-left ${estilo.wrapper}`}
              >
                <div className="flex items-center w-full">
                  <span className={`circulo-letra-enhanced w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 ${estilo.circle}`}>
                    {letras[i]}
                  </span>
                  <span className={`text-base md:text-lg flex-1 leading-tight ${estilo.text}`}>
                    {opcion}
                  </span>
                </div>
              </button>
            );
          })()
        ))}
      </div>

      {seleccionada && !respondida && (
        <button
          onClick={() => onResponder(seleccionada)}
          className="btn-primary mt-6 w-full font-gaming text-xs tracking-wider py-4"
        >
          CONFIRMAR RESPUESTA
        </button>
      )}

      {respondida && (
        <div className="max-w-[40rem] mx-auto mt-6 flex flex-col gap-4">
          {pregunta.info && (
            <div className="card-gaming text-left order-2 md:order-1">
              <p className="text-gaming-cyan text-sm font-gaming mb-2">DATO CURIOSO</p>
              <p className="font-main text-white/70 text-[17px] leading-relaxed">{pregunta.info}</p>
            </div>
          )}

          {onContinuar && (
            <button
              onClick={onContinuar}
              className="btn-primary w-full font-gaming text-sm tracking-wider py-4 order-1 md:order-2"
            >
              {puntosGanados === 0 && modalidad === 'nohit'
                ? 'VER RESULTADOS'
                : esUltima
                  ? 'VER RESULTADOS'
                  : esUltimaDelNivel
                    ? 'SIGUIENTE NIVEL →'
                    : 'SIGUIENTE PREGUNTA →'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Level1Trivia;
